import { useState, useCallback, useRef } from 'react';
import { BezierCurve } from './bezier';

interface DraggingState {
  pointIndex: number;
  type: 'point' | 'cp1' | 'cp2';
}

interface UseBezierEditorProps {
  controlPoints: BezierCurve;
  onControlPointsChange: (controlPoints: BezierCurve) => void;
  width: number;
  height: number;
  margin?: number;
  leftMargin?: number;
  rightMargin?: number;
  timeScale?: number;
  loadScale?: number;
  onHover?: (hour: number | null) => void;
}

interface UseBezierEditorReturn {
  svgRef: React.RefObject<SVGSVGElement>;
  dragging: DraggingState | null;
  timeToX: (time: number) => number;
  loadToY: (load: number) => number;
  handleMouseDown: (e: React.MouseEvent, pointIndex: number, type: 'point' | 'cp1' | 'cp2') => void;
  handleMouseMove: (e: React.MouseEvent) => void;
  handleMouseUp: () => void;
  handleHover: (e: React.MouseEvent) => void;
  handleMouseLeave: () => void;
}

export function useBezierEditor({
  controlPoints,
  onControlPointsChange,
  width,
  height,
  margin = 40,
  leftMargin,
  rightMargin,
  timeScale = 24,
  loadScale = 100,
  onHover,
}: UseBezierEditorProps): UseBezierEditorReturn {
  const [dragging, setDragging] = useState<DraggingState | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  // Use specific margins if provided, otherwise use general margin
  const actualLeftMargin = leftMargin ?? margin;
  const actualRightMargin = rightMargin ?? margin;
  const actualTopMargin = margin;
  const actualBottomMargin = margin;

  // Convert time to SVG x coordinate
  const timeToX = (time: number) => (time / timeScale) * (width - actualLeftMargin - actualRightMargin) + actualLeftMargin;
  
  // Convert load to SVG y coordinate
  const loadToY = (load: number) => height - actualBottomMargin - (load / loadScale) * (height - actualTopMargin - actualBottomMargin);
  
  // Convert SVG coordinates back to time/load
  const xToTime = (x: number) => ((x - actualLeftMargin) / (width - actualLeftMargin - actualRightMargin)) * timeScale;
  const yToLoad = (y: number) => ((height - actualBottomMargin - y) / (height - actualTopMargin - actualBottomMargin)) * loadScale;

  const handleMouseDown = useCallback((
    e: React.MouseEvent,
    pointIndex: number,
    type: 'point' | 'cp1' | 'cp2'
  ) => {
    e.preventDefault();
    setDragging({ pointIndex, type });
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!dragging || !svgRef.current) return;

    const rect = svgRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    const time = Math.max(0, Math.min(timeScale, xToTime(x)));
    const load = Math.max(0, Math.min(loadScale, yToLoad(y)));

    // Update hover hour while dragging
    if (onHover && time >= 0 && time <= timeScale) {
      const hour = Math.round(time);
      onHover(Math.max(0, Math.min(timeScale - 1, hour)));
    }

    const newPoints = [...controlPoints];
    const point = newPoints[dragging.pointIndex];
    
    if (dragging.type === 'point') {
      // Calculate the offset from the current position
      const deltaX = time - point.x;
      const deltaY = load - point.y;
      
      // Move the main point
      point.x = time;
      point.y = load;
      
      // Move the control points by the same offset to maintain relative position
      point.cp1x += deltaX;
      point.cp1y += deltaY;
      point.cp2x += deltaX;
      point.cp2y += deltaY;
    } else if (dragging.type === 'cp1') {
      // Move cp1 to the new position
      point.cp1x = time;
      point.cp1y = load;
      
      // Calculate the vector from main point to cp1
      const vectorX = point.cp1x - point.x;
      const vectorY = point.cp1y - point.y;
      
      // Move cp2 to the opposite side of the main point to maintain a straight line
      point.cp2x = point.x - vectorX;
      point.cp2y = point.y - vectorY;
    } else if (dragging.type === 'cp2') {
      // Move cp2 to the new position
      point.cp2x = time;
      point.cp2y = load;
      
      // Calculate the vector from main point to cp2
      const vectorX = point.cp2x - point.x;
      const vectorY = point.cp2y - point.y;
      
      // Move cp1 to the opposite side of the main point to maintain a straight line
      point.cp1x = point.x - vectorX;
      point.cp1y = point.y - vectorY;
    }
    
    onControlPointsChange(newPoints);
  }, [dragging, controlPoints, onControlPointsChange, xToTime, yToLoad, onHover, timeScale]);

  const handleMouseUp = useCallback(() => {
    setDragging(null);
  }, []);

  const handleHover = useCallback((e: React.MouseEvent) => {
    if (!svgRef.current || !onHover) return;

    const rect = svgRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    
    const time = xToTime(x);
    
    // Only trigger hover if within valid time range
    if (time >= 0 && time <= timeScale) {
      const hour = Math.round(time);
      onHover(Math.max(0, Math.min(timeScale - 1, hour)));
    }
  }, [onHover, xToTime, timeScale]);

  const handleMouseLeave = useCallback(() => {
    if (onHover) {
      onHover(null);
    }
  }, [onHover]);

  return {
    svgRef,
    dragging,
    timeToX,
    loadToY,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleHover,
    handleMouseLeave,
  };
}